<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import {
    formatPercentChange,
    percentChange,
  } from '@/domain/menu-import/live-update';
  import type { ILivePrice } from '@/domain/menu-import/live-rows';
  import { formatMoney } from '@/domain/store-setup/money';

  /**
   * The price column of a dish already on the menu (plan §3.5 «Dish rows»):
   * today's price, or, when publishing changes it, today's struck with the
   * percent and the new price below. Screen readers hear «Antes {from},
   * ahora {to}».
   */
  const props = defineProps<{
    price: ILivePrice;
    currency: string;
    country: string;
  }>();
  const proxy = getCurrentInstance()!.proxy;

  function money(value: number): string {
    return formatMoney(value, props.currency, props.country);
  }

  const percent = computed(() => {
    const { today, next } = props.price;
    if (next === null) return null;
    const fraction = percentChange(today, next);
    return fraction === null
      ? null
      : formatPercentChange(fraction, proxy.$i18n.locale);
  });
</script>

<template>
  <span class="v2-menu-live-price">
    <span v-if="price.next === null" class="price-now">{{
      money(price.today)
    }}</span>
    <template v-else>
      <span class="price-was" aria-hidden="true">
        <s class="was-value">{{ money(price.today) }}</s>
        <span v-if="percent" class="was-percent">{{ percent }}</span>
      </span>
      <span class="price-now" aria-hidden="true">{{ money(price.next) }}</span>
      <span class="sr-text">{{
        $t('menuImport.update.priceAria', {
          from: money(price.today),
          to: money(price.next),
        })
      }}</span>
    </template>
  </span>
</template>

<style scoped lang="scss">
  .v2-menu-live-price {
    @apply tw-inline-flex tw-flex-col tw-items-end tw-whitespace-nowrap tw-tabular-nums;

    .price-was {
      @apply tw-flex tw-items-baseline tw-gap-1.5 tw-text-sm tw-font-normal;
    }
    .was-value {
      @apply tw-text-neutral-400;
    }
    .was-percent {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .price-now {
      @apply tw-font-semibold;
    }
    .sr-text {
      @apply tw-sr-only;
    }
  }
</style>
